import { useCurrentAssistant } from '@/hooks/useAssistantAuth';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import {
  LayoutDashboard,
  XCircle,
  MenuSquare,
  Package,
  Users,
  Calculator,
} from 'lucide-react';
import type { AssistantPermissions } from '@/types/entities';

export default function AssistantDashboard() {
  const { t } = useTranslation();
  const { data: assistant } = useCurrentAssistant();
  const navigate = useNavigate();

  // Permission helper function
  const canViewPage = (page: keyof AssistantPermissions): boolean => {
    if (!assistant?.permissions || !assistant.permissions[page]) return false;
    const pagePermissions = assistant.permissions[page] as Record<string, boolean>;
    return Object.values(pagePermissions).some((val) => val === true);
  };

  // Check if assistant can view accounting (has salaries OR expenses permission)
  const canViewAccounting = (): boolean => {
    return canViewPage('salaries') || canViewPage('expenses');
  };

  // Define available pages with icons and routes
  const pages = [
    {
      key: 'dashboard' as const,
      icon: LayoutDashboard,
      titleKey: 'nav.dashboard',
      descriptionKey: 'assistant.dashboardDescription',
      route: '/dashboard',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950',
    },
    {
      key: 'cancellations' as const,
      icon: XCircle,
      titleKey: 'nav.cancellations',
      descriptionKey: 'assistant.cancellationsDescription',
      route: '/admin-cancellations',
      color: 'text-red-600',
      bgColor: 'bg-red-50 dark:bg-red-950',
    },
    {
      key: 'menu' as const,
      icon: MenuSquare,
      titleKey: 'nav.menu',
      descriptionKey: 'assistant.menuDescription',
      route: '/menu',
      color: 'text-green-600',
      bgColor: 'bg-green-50 dark:bg-green-950',
    },
    {
      key: 'stock' as const,
      icon: Package,
      titleKey: 'nav.inventory',
      descriptionKey: 'assistant.stockDescription',
      route: '/inventory',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950',
    },
    {
      key: 'staff' as const,
      icon: Users,
      titleKey: 'nav.staff',
      descriptionKey: 'assistant.staffDescription',
      route: '/staff',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50 dark:bg-indigo-950',
    },
    {
      key: 'accounting' as const,
      icon: Calculator,
      titleKey: 'nav.accounting',
      descriptionKey: 'assistant.accountingDescription',
      route: '/accounting',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
      accountingPermission: true, // Special flag for accounting
    },
  ];

  // Filter pages based on permissions
  const accessiblePages = pages.filter((page) => {
    // Special handling for accounting (needs salaries OR expenses permission)
    if ('accountingPermission' in page && page.accountingPermission) {
      return canViewAccounting();
    }
    return canViewPage(page.key as keyof AssistantPermissions);
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {t('assistant.welcome')}, {assistant?.username}
          </h1>
          <p className="text-muted-foreground">
            {assistant?.roleName} • {t('assistant.selectPage')}
          </p>
        </div>

        {accessiblePages.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-muted-foreground">
                {t('assistant.noPermissions') ||
                  'You do not have access to any pages. Please contact your administrator.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {accessiblePages.map((page) => {
              const Icon = page.icon;
              return (
                <Card
                  key={page.key}
                  className="hover:shadow-lg transition-all cursor-pointer hover:scale-105"
                  onClick={() => navigate({ to: page.route })}
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center ${page.bgColor}`}
                      >
                        <Icon className={`w-6 h-6 ${page.color}`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {t(page.titleKey) || page.titleKey}
                        </CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      {t(page.descriptionKey) ||
                        'Access this page with your assigned permissions'}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
